import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';

import PsychologyIcon from '@mui/icons-material/Psychology';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import echobuddyLogo from "../../assets/newEchoLogo.png";

const COLORS = {
    primary: "#2ABFAB",
    primaryDark: "#1EA898",
    purple: "#6C63FF",
    blue: "#3B7DDD",
    bg: "#F5F7FA",
    card: "#FFFFFF",
    border: "#E2E8F0",
    textDark: "#1A202C",
    textMid: "#4A5568",
    textLight: "#718096",
};

const AssessmentCard = ({ title, description, icon: IconComponent, color, onClick, isMobile }) => {
    return (
        <Card
            onClick={onClick}
            sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : '280px',
                borderRadius: 3,
                background: '#ffffff',
                border: '1px solid #f0f2f5',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                    borderColor: `${color}40`
                }
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                    }}>
                        <IconComponent sx={{ color: color, fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: COLORS.textDark, fontWeight: 600, fontSize: '0.95rem' }}>
                        {title}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{
                    color: COLORS.textLight,
                    mb: 2,
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                }}>
                    {description}
                </Typography>
                <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{
                        py: 0.75,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        color: '#fff',
                        borderColor: `${color}30`,
                        background: `linear-gradient(135deg, ${color}, ${color}CC)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${color}CC, ${color})`,
                            borderColor: `${color}60`,
                            boxShadow: `0 4px 12px ${color}30`,
                        }
                    }}
                >
                    Start Assessment
                </Button>
            </CardContent>
        </Card>
    );
};

const AssessmentSelection = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSubjectiveWellBeing = () => {
        navigate('/HealthSurvey');
    };

    const handleEAPWellnessIndex = () => {
        navigate('/health-survey');
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: '#f8f9ff',
                pt: 4,
                pb: 6,
                position: 'relative',
            }}
        >
            <div style={{ position: "absolute", right: 24, top: 24, zIndex: 1000 }}>
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        color: "#7843f3",
                        textTransform: 'none',
                        fontWeight: 500,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 14,
                        padding: '8px 16px',
                        borderRadius: 8,
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 14 }} />
                    Back
                </button>
            </div>

            <Container maxWidth="md" sx={{ mt: isMobile ? 12 : 10 }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: isMobile ? '1.75rem' : '2.25rem',
                        }}
                    >
                        Choose Your Assessment
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: COLORS.textMid,
                            lineHeight: 1.6,
                            maxWidth: '600px',
                            mx: 'auto',
                            fontSize: isMobile ? '0.95rem' : '1rem',
                        }}
                    >
                        Select an assessment type to begin your wellness journey. Each assessment is designed to help you understand and improve different aspects of your well-being.
                    </Typography>
                </Box>

                {/* Assessment Cards */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 3,
                        justifyContent: 'center',
                        mb: 4,
                    }}
                >
                    <AssessmentCard
                        title="Subjective Well-Being"
                        description="A comprehensive self-assessment based on international scales to evaluate your personal well-being across life experiences."
                        icon={PsychologyIcon}
                        color="#22c55e"
                        onClick={handleSubjectiveWellBeing}
                        isMobile={isMobile}
                    />
                    <AssessmentCard
                        title=" Wellness Index"
                        description="Assess your wellness across emotional, mental, physical, social, and financial dimensions with personalized insights."
                        icon={AssessmentIcon}
                        color="#8b5cf6"
                        onClick={handleEAPWellnessIndex}
                        isMobile={isMobile}
                    />
                </Box>
            </Container>
        </Box>
    );
};

export default AssessmentSelection;
